import { useEffect, useState } from "react";
import { localStorage } from "dash-ui";

export interface UseRestCallParams<T> {
  // The REST API endpoint (without the base URL)
  endpoint: string;
  // HTTP method, defaulted to POST
  method?: string;
  // Optional wrapper key for the response data
  dataWrapper?: string;
  // The payload for the request
  body?: T;
  // Option to skip making the call
  skip?: boolean;
}

export interface UseRestCallResult<U> {
  data: U | null;
  loading: boolean;
  error: any;
}

/**
 * A generic hook for making REST API calls.
 *
 * T: the type of the request body.
 * U: the type of the response.
 */
export function useRestCall<T = any, U = any>({
  endpoint,
  method = "POST",
  body,
  dataWrapper,
  skip = false,
}: UseRestCallParams<T>): UseRestCallResult<U> {
  const [data, setData] = useState<U | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (skip) return;
    let isMounted = true;

    async function fetchData() {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env["REACT_APP_REST"]}/${endpoint}`,
          {
            method,
            headers: {
              accessToken: localStorage.getAccessToken() || "",
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          }
        );

        if (!response.ok) {
          throw new Error(`Network response was not ok`);
        }

        const responseData: U = await response.json();
        if (isMounted) {
          if (dataWrapper) {
            setData({ [dataWrapper]: responseData } as unknown as U);
          } else {
            setData(responseData);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [endpoint, method, JSON.stringify(body), skip, dataWrapper]);

  return { data, loading, error };
}
